import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndexPage() {
  usePageMeta("Blog", "News, culture and behind-the-scenes from USDD Academy.");
  const [posts, setPosts] = useState([]);
  useEffect(() => { axios.get(`${API}/posts`).then((r) => setPosts(r.data)).catch(() => {}); }, []);

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Blog · News · Culture"
          title="The"
          highlight="USDD Journal."
          description="Stories, lessons and behind-the-scenes from the floor. Culture, technique, community and everything in between."
          ctaPrimary={{ label: "Subscribe — Start Your Journey", to: "/journey" }}
          ctaSecondary={{ label: "See events", to: "/events" }}
          bg={SITE.classImg5}
        />
        <section className="py-16 sm:py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  data-testid={`post-card-${p.slug}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-brand-yellow/40 bg-zinc-950/60 transition-all hover:-translate-y-1"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image || SITE.classImg7}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover brightness-[0.55] group-hover:brightness-75 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">{p.author}</div>
                    <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{p.title}</h3>
                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow hover:gap-3 transition-all"
                    >
                      Read article <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </motion.article>
              ))}
              {posts.length === 0 && (
                <p className="text-zinc-400 col-span-full">No posts yet — check back soon.</p>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
